import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import styles from './MarketPlaceCards.module.css';
import AuthContext from '../../../context/AuthContext';
import useConnectedStores from '../../../hooks/useConnectedStores';
import StoreSwitcher from './StoreSwitcher';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const BASE_PATH = '/auth/vendor-central';
const ID_FIELD = 'vendor_id';

const call = async (token, path, { method = 'GET' } = {}) => {
    const response = await fetch(`${API_URL}${BASE_PATH}${path}`, {
        method,
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(data.error || `Request failed (${response.status})`);
    return data;
};

const VendorLogo = () => (
    <svg width="46" height="46" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M3 9.5 12 4l9 5.5V20a1 1 0 0 1-1 1h-5v-6H9v6H4a1 1 0 0 1-1-1V9.5Z"
              stroke="#0e0e0e" strokeWidth="1.6" strokeLinejoin="round" />
    </svg>
);

const ConnectionDetails = ({ data, hideStoreName }) => {
    if (!data || (!data.vendor_id && !data.store_name && !data.marketplace_id)) return null;
    return (
        <div className={styles.connectionDetails}>
            {data.store_name && !hideStoreName && (
                <div className={styles.detailItem}>
                    <span className={styles.detailLabel}>Store:</span>
                    <span className={styles.detailValue}>{data.store_name}</span>
                </div>
            )}
            {data.vendor_id && (
                <div className={styles.detailItem}>
                    <span className={styles.detailLabel}>Vendor ID:</span>
                    <span className={styles.detailValue}>{data.vendor_id}</span>
                </div>
            )}
            {data.marketplace_id && (
                <div className={styles.detailItem}>
                    <span className={styles.detailLabel}>Marketplace:</span>
                    <span className={styles.detailValue}>{data.marketplace_id}</span>
                </div>
            )}
        </div>
    );
};

const VendorCentralCard = () => {
    const queryClient = useQueryClient();
    const authCtx = useContext(AuthContext);
    const [connected, setConnected] = useState(false);
    const [vendorData, setVendorData] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isConnecting, setIsConnecting] = useState(false);

    const getToken = useCallback(() => authCtx.token, [authCtx.token]);

    // Vendor data is scoped to the vendor account, so a switch or disconnect
    // invalidates every cached query the same way the seller card does.
    const onChange = useCallback(() => {
        queryClient.invalidateQueries();
    }, [queryClient]);

    const {
        stores, activeId, isSwitching, error: switchError, setError,
        loadStores, switchStore, disconnectStore,
    } = useConnectedStores({ basePath: BASE_PATH, idField: ID_FIELD, getToken, onChange });

    // Unlike the seller flow, finalize is synchronous — it is one SP-API call,
    // so there is no queued sync to poll for.
    const checkStatus = useCallback(async () => {
        if (!authCtx.token) {
            setIsLoading(false);
            return;
        }
        try {
            const data = await call(authCtx.token, '/status');
            if (data.connected) {
                setConnected(true);
                setVendorData(data);
                if (data.store_name === 'Pending' || data.marketplace_id === 'Pending') {
                    setIsConnecting(true);
                    try {
                        const finalized = await call(authCtx.token, '/finalize', { method: 'POST' });
                        setVendorData({ connected: true, ...finalized });
                        await loadStores();
                    } catch (finalizeError) {
                        setError(finalizeError.message);
                    } finally {
                        setIsConnecting(false);
                    }
                }
            } else {
                setConnected(false);
                setVendorData(null);
            }
        } catch (err) {
            setConnected(false);
            setVendorData(null);
        } finally {
            setIsLoading(false);
        }
    }, [authCtx.token, loadStores, setError]);

    useEffect(() => {
        setIsConnecting(false);

        const params = new URLSearchParams(window.location.search);
        if (params.has('vendor_central_connected')) {
            params.delete('vendor_central_connected');
            window.history.replaceState({}, '', params.toString()
                ? `${window.location.pathname}?${params.toString()}`
                : window.location.pathname);
        }

        checkStatus();
        loadStores();

        const refresh = () => {
            setIsConnecting(false);
            checkStatus();
            loadStores();
        };
        const onVisibility = () => { if (!document.hidden) refresh(); };
        document.addEventListener('visibilitychange', onVisibility);
        window.addEventListener('focus', refresh);
        return () => {
            document.removeEventListener('visibilitychange', onVisibility);
            window.removeEventListener('focus', refresh);
        };
    }, [checkStatus, loadStores]);

    const handleConnect = useCallback(async () => {
        if (!authCtx.token) return;
        setIsConnecting(true);
        try {
            const data = await call(authCtx.token, '/login');
            if (data.auth_url) {
                window.location.href = data.auth_url;
                return;
            }
            setIsConnecting(false);
        } catch (err) {
            setError(err.message);
            setIsConnecting(false);
        }
    }, [authCtx.token, setError]);

    const handleDisconnect = useCallback(async () => {
        try {
            const result = await disconnectStore(activeId);
            const stillConnected = !!result?.vendor_central_status;
            setConnected(stillConnected);
            setVendorData(null);
            if (stillConnected) checkStatus();
        } catch (err) {
            setError(err.message);
        }
    }, [activeId, disconnectStore, checkStatus, setError]);

    const hasConnectedBefore = vendorData?.vendor_id || stores.length > 0;
    const buttonText = isLoading ? 'Loading...'
        : isConnecting ? 'Connecting...'
        : isSwitching ? 'Switching...'
        : hasConnectedBefore ? (connected ? 'Disconnect' : 'Reconnect') : 'Connect';
    const buttonAction = hasConnectedBefore && connected ? handleDisconnect : handleConnect;

    return (
        <div className={`${styles.card} ${styles.vendorCard}`}>
            {hasConnectedBefore && (
                <div className={styles.connectedBadge}>
                    {connected ? 'Connected' : 'Token Expired'}
                </div>
            )}

            <div>
                <div className={`${styles.logoContainer} ${styles.vendorLogo}`}>
                    <VendorLogo />
                </div>
                <h3 className={styles.title}>Amazon Vendor</h3>
                <StoreSwitcher
                    id="vendor-main-account"
                    stores={stores}
                    activeId={activeId}
                    idField={ID_FIELD}
                    isSwitching={isSwitching}
                    onChange={(e) => switchStore(e.target.value)}
                />
                {switchError && <div className={styles.switchError}>{switchError}</div>}
                <ConnectionDetails data={vendorData} hideStoreName={stores.length > 0} />
            </div>

            <div className={styles.actions}>
                <button
                    className={styles.button}
                    onClick={buttonAction}
                    disabled={isLoading || isConnecting || isSwitching}
                    aria-label={`${buttonText} Amazon Vendor account`}
                >
                    {buttonText}
                </button>
                {connected && (
                    <button
                        className={styles.secondaryButton}
                        onClick={handleConnect}
                        disabled={isConnecting || isSwitching}
                        aria-label="Connect another Amazon Vendor account"
                    >
                        + Connect another account
                    </button>
                )}
            </div>
        </div>
    );
};

export default VendorCentralCard;
